import { AxiosError } from 'axios';
import {
  ApiErrorResponse,
  ErrorType,
  ErrorDomain,
  ParsedError,
  LegacyErrorResponse,
} from '../types/errors';

/**
 * Main error parser - handles both new structured format and legacy format
 */
export function parseApiError(error: AxiosError): ParsedError {
  const httpStatus = error.response?.status || 500;
  const responseData = error.response?.data;

  // Check if it's the new structured format
  if (responseData && typeof responseData === 'object' && 'code' in responseData) {
    return parseStructuredError(responseData as ApiErrorResponse, httpStatus);
  }

  // Check if it's the legacy format
  if (responseData && typeof responseData === 'object' && 'error' in responseData) {
    return parseLegacyError(responseData as LegacyErrorResponse, httpStatus);
  }

  // Handle network errors
  if (!error.response) {
    return {
      type: ErrorType.NETWORK_ERROR,
      domain: ErrorDomain.UNKNOWN,
      code: 'NETWORK_ERROR',
      message: 'Erro de conexão. Verifique sua internet e tente novamente.',
      httpStatus: 0,
      isRetryable: true,
    };
  }

  // Fallback for unknown error format
  return {
    type: ErrorType.UNKNOWN,
    domain: ErrorDomain.UNKNOWN,
    code: 'UNKNOWN_ERROR',
    message: 'Erro desconhecido. Tente novamente.',
    httpStatus,
    isRetryable: false,
  };
}

/**
 * Returns a backend message only when it was explicitly marked safe to display.
 *
 * 401/403 bodies are masked by default because RequireCustomerAnyRole (and friends) put the
 * customer UUID and the required-role list in them — e.g.
 * " Permissões insuficientes para o cliente 90cbe66d-… Necessária uma das seguintes funções:
 * [admin supervisor sindico]". A handler that has written a sentence fit for an operator sets
 * `user_message`, and only that field is trusted here; `error`/`message` never are for these
 * statuses. Generic middleware must never set it.
 */
function safeMessage(body: { user_message?: string }): string | undefined {
  const candidate = body?.user_message;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
}

/**
 * Parse new structured error format
 */
function parseStructuredError(
  errorResponse: ApiErrorResponse,
  httpStatus: number
): ParsedError {
  const domain = extractDomain(errorResponse.code);
  const type = classifyErrorType(errorResponse.code, httpStatus);
  const isRetryable = isRetryableError(type, httpStatus);

  // Never expose raw backend permission/auth messages to users.
  // 403 responses from the backend carry role lists and internals — replace with friendly text,
  // UNLESS the handler explicitly marked a user-appropriate sentence via `user_message`.
  const message = type === ErrorType.PERMISSION_DENIED
    ? safeMessage(errorResponse) ?? 'Você não tem permissão para realizar esta ação.'
    : errorResponse.message;

  return {
    type,
    domain,
    code: errorResponse.code,
    message,
    field: errorResponse.field,
    details: errorResponse.details,
    httpStatus,
    isRetryable,
  };
}

/**
 * Parse legacy error format
 */
function parseLegacyError(
  errorResponse: LegacyErrorResponse,
  httpStatus: number
): ParsedError {
  const type = classifyErrorTypeByStatus(httpStatus);

  // Map to user-friendly messages. Never expose raw backend auth/permission messages.
  // 401 = session expired; 403 = permission denied (different from session expiry).
  // `user_message` is the one opt-in escape hatch — see safeMessage below.
  const message = httpStatus === 401
    ? safeMessage(errorResponse) ?? 'Sessão expirada. Por favor, faça login novamente.'
    : httpStatus === 403
    ? safeMessage(errorResponse) ?? 'Você não tem permissão para realizar esta ação.'
    : (errorResponse.message || errorResponse.error);

  return {
    type,
    domain: ErrorDomain.UNKNOWN,
    code: 'LEGACY_ERROR',
    message,
    httpStatus,
    isRetryable: isRetryableError(type, httpStatus),
  };
}

/**
 * Extract domain from error code (e.g., "PERSON" from "PERSON_VALIDATION_001")
 */
function extractDomain(code: string): ErrorDomain {
  const parts = code.split('_');
  if (parts.length === 0) return ErrorDomain.UNKNOWN;

  const domainStr = parts[0].toUpperCase();

  if (domainStr in ErrorDomain) {
    return ErrorDomain[domainStr as keyof typeof ErrorDomain];
  }

  return ErrorDomain.UNKNOWN;
}

/**
 * Classify error type based on error code and HTTP status
 */
function classifyErrorType(code: string, httpStatus: number): ErrorType {
  // Check code patterns
  if (code.includes('VALIDATION')) return ErrorType.VALIDATION_ERROR;
  if (code.includes('NOT_FOUND')) return ErrorType.NOT_FOUND;
  if (code.includes('CONFLICT')) return ErrorType.CONFLICT;
  if (code.includes('PERMISSION') || code.includes('DENIED') || code.includes('FORBIDDEN')) return ErrorType.PERMISSION_DENIED;
  if (code.includes('UNAUTHORIZED')) return ErrorType.AUTH_ERROR;
  if (code.includes('PRECONDITION')) return ErrorType.PRECONDITION_FAILED;
  if (code.includes('SERVER')) return ErrorType.SERVER_ERROR;

  // Fallback to HTTP status classification
  return classifyErrorTypeByStatus(httpStatus);
}

/**
 * Classify error type based only on HTTP status (for legacy errors)
 */
function classifyErrorTypeByStatus(httpStatus: number): ErrorType {
  if (httpStatus === 400) return ErrorType.VALIDATION_ERROR;
  if (httpStatus === 401) return ErrorType.AUTH_ERROR;
  if (httpStatus === 403) return ErrorType.PERMISSION_DENIED;
  if (httpStatus === 404) return ErrorType.NOT_FOUND;
  if (httpStatus === 409) return ErrorType.CONFLICT;
  if (httpStatus === 412) return ErrorType.PRECONDITION_FAILED;
  if (httpStatus >= 500) return ErrorType.SERVER_ERROR;

  return ErrorType.UNKNOWN;
}

/**
 * Determine if an error is retryable
 */
function isRetryableError(type: ErrorType, httpStatus: number): boolean {
  // Network errors are retryable
  if (type === ErrorType.NETWORK_ERROR) return true;

  // Server errors (5xx) are generally retryable
  if (httpStatus >= 500) return true;

  // 503 Service Unavailable is retryable
  if (httpStatus === 503) return true;

  // Other errors are not retryable
  return false;
}

/**
 * Get user-friendly message from error
 */
export function getUserMessage(error: AxiosError): string {
  const parsed = parseApiError(error);
  return parsed.message;
}

/**
 * Get error details for debugging
 */
export function getErrorDetails(error: AxiosError): ParsedError {
  return parseApiError(error);
}

/**
 * Check if error is retryable
 */
export function isRetryable(error: AxiosError): boolean {
  const parsed = parseApiError(error);
  return parsed.isRetryable;
}

/**
 * Converts raw device communication error strings (from Electron deviceHttpRequest)
 * to user-friendly Portuguese messages. Never exposes technical jargon to end users.
 */
export function friendlyDeviceError(error: string | undefined | null): string {
  if (!error) return 'Dispositivo não respondeu';

  const e = error.toLowerCase();

  if (e.includes('request timeout') || e.includes('timeout')) {
    return 'Dispositivo não respondeu — verifique se está ligado e acessível na rede';
  }
  if (e.includes('econnrefused')) {
    return 'Conexão recusada pelo dispositivo — verifique o endereço IP e se o serviço está ativo';
  }
  if (e.includes('etimedout')) {
    return 'Tempo de conexão esgotado — o dispositivo pode estar desligado ou fora da rede';
  }
  if (e.includes('ehostunreach') || e.includes('enetunreach')) {
    return 'Dispositivo inacessível — verifique se está na mesma rede';
  }
  if (e.includes('enotfound')) {
    return 'Endereço do dispositivo não encontrado — verifique o IP configurado';
  }
  if (e.includes('econnreset')) {
    return 'Conexão interrompida pelo dispositivo';
  }
  if (e.includes('digest authentication failed') || e.includes('authentication failed')) {
    return 'Falha de autenticação — verifique o usuário e senha do dispositivo';
  }
  if (e.includes('authentication required')) {
    return 'Autenticação necessária — configure as credenciais do dispositivo';
  }

  return 'Erro de comunicação com o dispositivo';
}
